'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useMemo } from 'react';

import cn from 'classnames';

import Trash from '@publicImages/svg/trash.svg?icon';

import AccessKeyErrorModal from '@/components/AccessKeyErrorModal';
import IconButton from '@/components/elements/IconButton';
import { Skeleton } from '@/components/Skeleton';
import useCalculateInvoiceAndHoldIsPending from '@/containers/VoyagePlanner/Summary/hooks/useCalculateInvoiceAndHoldIsPending';
import { ACCESS_KEY_SOURCE, getIsAccessKeyStateValid, selectAccessKeySlice } from '@/ducks/accessKeys';
import { ACCESS_KEY_PARAM } from '@/ducks/accessKeys/constants';
import { resetAKFlowFully } from '@/ducks/accessKeys/utils';
import { useUIResourcePool } from '@/ducks/common/resources';
import { selectIsPersistentReady } from '@/ducks/common/selectors';
import { routes } from '@/ducks/routes';
import { useAppDispatch, useAppSelector } from '@/store';
import tagmanager from '@/tagmanager';

import styles from './AccessKey.module.scss';

const MARQUEE_UI_RESOURCE_POOL = {
  clearCTA: 'AccessKey.clearCTA',
  defaultDescription: 'AccessKey.bannerMessage',
  defaultTitle: 'AccessKey.eyebrow.label',
  mobileClearText: 'AccessKey.clearCTA.short',
  promoIcon: 'AccessKey.emoji.key',
  promoText: 'AccessKey.bookShareToast',
  title: 'AccessKey.bannerTitle',
} as const;

type TProps = {
  className?: string;
  isErrorModalAllowed?: boolean;
  placeholder?: React.ReactNode;
};

const AccessKeyBanner = ({ className, isErrorModalAllowed, placeholder }: TProps) => {
  const dispatch = useAppDispatch();
  const hasAKParam = useSearchParams()?.has(ACCESS_KEY_PARAM);
  const accessKeyDetails = useAppSelector(selectAccessKeySlice);
  const isReady = useAppSelector(selectIsPersistentReady);
  const { error, shortDescription, source, state, title } = accessKeyDetails;
  const isManualErrorHandling = isErrorModalAllowed && source === ACCESS_KEY_SOURCE.URL;
  const isValid = getIsAccessKeyStateValid(state);
  const canFollowPending = usePathname() === routes.planner.summary.path;
  const isPending = useCalculateInvoiceAndHoldIsPending(true);
  const messages = useUIResourcePool(MARQUEE_UI_RESOURCE_POOL);

  useEffect(() => {
    if (isReady && !canFollowPending && error && !isManualErrorHandling) dispatch(resetAKFlowFully());
  }, [canFollowPending, error, isManualErrorHandling, isReady]);

  const canUseSkeleton = useMemo(() => !isReady && hasAKParam, [isValid]);

  if (error && isManualErrorHandling) return <AccessKeyErrorModal />;

  const clearAccessKey = () => {
    const buttonText = messages.clearCTA;
    tagmanager.tracker.common.trackIconClick({
      labels: [buttonText],
    });
    dispatch(resetAKFlowFully());
  };

  if (!isValid && !canUseSkeleton) return placeholder;

  return (
    <div className={cn(styles.root, className)}>
      {!isValid && canUseSkeleton && (
        <>
          <Skeleton containerClassName={styles.skeleton} />
          <Skeleton containerClassName={styles.skeleton} />
        </>
      )}
      {isValid && (
        <>
          <div className={styles.description}>
            <span className={styles.icon}>{messages.promoIcon}</span>
            <div>
              <span className={styles.title}>{title ?? messages.defaultTitle} </span>
              &nbsp;
              <span>{shortDescription ?? messages.defaultDescription}</span>
            </div>
          </div>
          <div className={styles.dividedLine}>
            <div className={styles.cta}>
              <IconButton
                aria-label={messages.clearCTA}
                className={styles.clearButton}
                disabled={canFollowPending && isPending}
                onClick={clearAccessKey}
              >
                <Trash />
                <span className={styles.label}>{messages.clearCTA}</span>
              </IconButton>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AccessKeyBanner;
